import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { JURISDICTIONS, getJurisdictionLabel } from "../../../constants/compliance";
import { DashboardLayout } from "../../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { StatsCard } from "../../../components/shared/StatsCard";
import { dsrV2API, exportAPI, tasksAPI } from "../../../services/api";
import { TasksCountBadge } from "../../../components/shared/TasksCountBadge";
import { useAuth } from "../../../contexts/AuthContext";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import {
  UserCheck,
  Plus,
  Search,
  Clock,
  AlertTriangle,
  CheckCircle2,
  FileText,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Timer,
  RefreshCw,
  FileDown,
  Dot,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Checkbox } from "../../../components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "../../../lib/utils";
import { useRole } from "../../../hooks/useRole";
import { DSRAlertBanners } from "./components/DSRAlertBanners";
import { DSRKPIRow } from "./components/DSRKPIRow";
import { RequestsByTypeWidget } from "./components/RequestsByTypeWidget";
import { DeadlineTrackerWidget } from "./components/DeadlineTrackerWidget";
import { ByJurisdictionWidget } from "./components/ByJurisdictionWidget";
import { ActivityLogWidget } from "./components/ActivityLogWidget";
import { ComplianceRemindersWidget } from "./components/ComplianceRemindersWidget";

// Status badge colors
const statusStyles = {
  received: {
    bg: "bg-blue-100 dark:bg-blue-950",
    text: "text-blue-700 dark:text-blue-300",
    border: "border-l-blue-500",
  },
  verifying: {
    bg: "bg-amber-100 dark:bg-amber-950",
    text: "text-amber-700 dark:text-amber-300",
    border: "border-l-amber-500",
  },
  awaiting_info: {
    bg: "bg-orange-100 dark:bg-orange-950",
    text: "text-orange-700 dark:text-orange-300",
    border: "border-l-orange-500",
  },
  processing: {
    bg: "bg-purple-100 dark:bg-purple-950",
    text: "text-purple-700 dark:text-purple-300",
    border: "border-l-purple-500",
  },
  reviewing: {
    bg: "bg-indigo-100 dark:bg-indigo-950",
    text: "text-indigo-700 dark:text-indigo-300",
    border: "border-l-indigo-500",
  },
  responded: {
    bg: "bg-green-100 dark:bg-green-950",
    text: "text-green-700 dark:text-green-300",
    border: "border-l-green-500",
  },
  closed: { bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-700 dark:text-gray-300", border: "border-l-gray-500" },
  withdrawn: {
    bg: "bg-slate-100 dark:bg-slate-800",
    text: "text-slate-600 dark:text-slate-400",
    border: "border-l-slate-500",
  },
  refused: { bg: "bg-red-100 dark:bg-red-950", text: "text-red-700 dark:text-red-300", border: "border-l-red-500" },
};

const statusLabels = {
  received: "Received",
  verifying: "Verifying",
  awaiting_info: "Awaiting Info",
  processing: "Processing",
  reviewing: "Under Review",
  responded: "Responded",
  closed: "Closed",
  withdrawn: "Withdrawn",
  refused: "Refused",
};

const requestTypeLabels = {
  access: "Access",
  rectification: "Rectification",
  erasure: "Erasure",
  blocking: "Blocking",
  restriction: "Restriction",
  portability: "Portability",
  objection: "Objection",
  prevent_processing: "Prevent Processing",
  objection_marketing: "Marketing Opt-Out",
  automated_decision: "Automated Decision",
  withdraw_consent: "Withdraw Consent",
  complaint: "Complaint",
  other: "Other",
};

export default function DSRPageV2() {
  const navigate = useNavigate();
  const { isDPO, isComplianceReadOnly: isReadOnly } = useAuth();
  const { permissions, isViewer } = useRole();
  const { selectedClientId, selectedEntityId, entities } = useGlobalContext();
  const [dsrs, setDsrs] = useState([]);
  const [taskCounts, setTaskCounts] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedJurisdiction, setSelectedJurisdiction] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedDsr, setSelectedDsr] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dsrsRes, statsRes, countsRes] = await Promise.all([
        dsrV2API.getAll(selectedEntityId || undefined, selectedStatus || undefined, selectedType || undefined, selectedClientId || undefined),
        dsrV2API.getStats(selectedEntityId || undefined, selectedClientId || undefined),
        tasksAPI.getCountsBatch('dsr').catch(() => ({ data: {} })),
      ]);
      setDsrs(dsrsRes.data);
      setStats(statsRes.data);
      setTaskCounts(countsRes.data || {});
    } catch (error) {
      console.error("Failed to fetch DSR data:", error);
      toast.error("Failed to fetch DSR data");
    } finally {
      setLoading(false);
    }
  }, [selectedEntityId, selectedStatus, selectedType, selectedClientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this DSR?")) return;
    try {
      await dsrV2API.delete(id);
      toast.success("DSR deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete DSR");
    }
  };

  const handleExportDocx = async (dsrId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await exportAPI.dsrDocx(dsrId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || `DSR_${dsrId}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("DSR exported");
    } catch {
      toast.error("Failed to export DSR");
    }
  };

  const handleStatusChange = async (dsr, newStatus) => {
    try {
      await dsrV2API.update(dsr.id, { ...dsr, status: newStatus });
      toast.success("Status updated");
      setSummaryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openSummary = (dsr) => {
    setSelectedDsr(dsr);
    setSummaryDialogOpen(true);
  };

  const filteredDsrs = dsrs.filter((d) => {
    const searchMatch =
      d.requester?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.requester?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.dsr_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.request_summary?.toLowerCase().includes(searchTerm.toLowerCase());

    const overdueMatch = !overdueOnly || d.is_overdue;
    const jurisdictionMatch = !selectedJurisdiction || d.jurisdiction === selectedJurisdiction;

    return searchMatch && overdueMatch && jurisdictionMatch;
  });

  const getDeadlineDisplay = (dsr) => {
    const days = dsr.days_remaining;
    const deadline = dsr.deadline; // Use API's direct deadline field

    if (!deadline) return "-";

    if (dsr.status && ["responded", "closed", "withdrawn", "refused"].includes(dsr.status)) {
      return <span className="text-muted-foreground">Completed</span>;
    }

    if (days === null || days === undefined) return format(new Date(deadline), "MMM d, yyyy");

    if (days < 0) {
      return (
        <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400 font-medium">
          <AlertTriangle className="h-4 w-4" />
          <span>{Math.abs(days)}d overdue</span>
        </div>
      );
    }

    if (days <= 7) {
      return (
        <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-medium">
          <Clock className="h-4 w-4" />
          <span>{days}d left</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Timer className="h-4 w-4" />
        <span>{days}d left</span>
      </div>
    );
  };

  const getStalledStatus = (dsr) => {
    if (!dsr.updated_at) return null;
    const updatedDate = new Date(dsr.updated_at);
    const today = new Date();
    const daysSinceUpdate = Math.floor((today - updatedDate) / (1000 * 60 * 60 * 24));

    const isOpenStatus = dsr.status && !["responded", "closed", "withdrawn", "refused"].includes(dsr.status);

    if (!isOpenStatus || daysSinceUpdate < 7) return null;

    return {
      daysSinceUpdate,
      isVeryStalled: daysSinceUpdate >= 14,
    };
  };


  const getStageLabel = (dsr) => {
    const totalSteps = 6;
    const currentStep = dsr.current_step || 1;
    const stepNames = ["Intake", "Verification", "Search", "Processing", "Response", "Closure"];
    return `${stepNames[currentStep - 1] || "Step " + currentStep} ${currentStep}/${totalSteps}`;
  };

  const hasExtension = (dsr) => {
    return dsr.deadline_extended === true;
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="dsr-page-v2">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <UserCheck className="h-6 w-6 text-primary" />
              DSR Management
            </h1>
            <p className="text-muted-foreground">Handle Data Subject Requests with multi-jurisdiction compliance</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {!isReadOnly && permissions.canCreateRequest && (
              <Button onClick={() => navigate("/compliance/dsr/workflow/new")} data-testid="log-dsr-btn">
                <Plus className="h-4 w-4 mr-2" />
                Log New Request
              </Button>
            )}
          </div>
        </div>

        {/* Alert Banners */}
        {permissions.canViewAlerts && dsrs.length > 0 && <DSRAlertBanners dsrs={dsrs} />}

        {/* KPI Row - 5 Counters */}
        {dsrs.length > 0 && <DSRKPIRow dsrs={dsrs} entities={entities} />}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 bg-muted/50 p-4 rounded-lg">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-dsr-input"
            />
          </div>

          <Select value={selectedStatus || "all"} onValueChange={(val) => setSelectedStatus(val === "all" ? "" : val)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedType || "all"} onValueChange={(val) => setSelectedType(val === "all" ? "" : val)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {Object.entries(requestTypeLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {permissions.canSeeJurisdictionWidget && (
            <Select
              value={selectedJurisdiction || "all"}
              onValueChange={(val) => setSelectedJurisdiction(val === "all" ? "" : val)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Jurisdictions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jurisdictions</SelectItem>
                {JURISDICTIONS.map(j => (
                  <SelectItem key={j.value} value={j.value}>{j.label} {j.flag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex items-center space-x-2">
            <Checkbox id="overdue-filter" checked={overdueOnly} onCheckedChange={setOverdueOnly} />
            <label htmlFor="overdue-filter" className="text-sm cursor-pointer">
              Overdue Only
            </label>
          </div>
        </div>

        {/* Mid-row Widgets */}
        {dsrs.length > 0 && (
          <>
            {/* Deadline Tracker - Full Width */}
            <DeadlineTrackerWidget dsrs={dsrs} entities={entities} />

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RequestsByTypeWidget dsrs={dsrs} />
              {permissions.canSeeJurisdictionWidget && <ByJurisdictionWidget dsrs={dsrs} />}
            </div>
          </>
        )}

        {/* DSR Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Reference</TableHead>
                  {isDPO && <TableHead>Client</TableHead>}
                  {permissions.canSeePII && <TableHead>Requester</TableHead>}
                  <TableHead>Type</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Statutory Deadline</TableHead>
                  {permissions.canSeeJurisdictionWidget && <TableHead>Jurisdiction</TableHead>}
                  {permissions.canSeeFlags && <TableHead>Flags</TableHead>}
                  <TableHead>Stage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw className="h-5 w-5 animate-spin" />
                        Loading...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredDsrs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="p-0 border-0">
                      <div className="py-12 px-6">
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="relative mb-6">
                            <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-xl" />
                            <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-500/10 to-teal-600/10 border border-teal-500/20">
                              <FileText className="h-8 w-8 text-teal-600 dark:text-teal-400" />
                            </div>
                          </div>
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                            No data subject requests
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-6">
                            Track and manage access, erasure, and other data subject rights requests in compliance with
                            GDPR requirements.
                          </p>
                          {!isReadOnly && (
                            <Button
                              onClick={() => navigate("/compliance/dsr/workflow/new")}
                              className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Log New Request
                            </Button>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDsrs.map((dsr) => (
                    <TableRow
                      key={dsr.id}
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        dsr.is_overdue && "bg-red-50/50 dark:bg-red-950/20",
                      )}
                      onClick={() => openSummary(dsr)}
                      data-testid={`dsr-row-${dsr.id}`}
                    >
                      <TableCell className="font-mono text-sm font-medium">{dsr.dsr_reference || "-"}</TableCell>
                      {isDPO && (
                        <TableCell className="max-w-[150px] truncate">
                          {dsr.entity_name || getOrgName(dsr.organization_id)}
                        </TableCell>
                      )}
                      {permissions.canSeePII && (
                        <TableCell>
                          <div>
                            <p className="font-medium">{dsr.requester?.name || "-"}</p>
                            <p className="text-xs text-muted-foreground">{dsr.requester?.email || ""}</p>
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {requestTypeLabels[dsr.request_type] || dsr.request_type}
                          </Badge>
                          <TasksCountBadge counts={taskCounts[dsr.id]} showStatusInline />
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {dsr.date_received ? format(new Date(dsr.date_received), "MMM d, yyyy") : "-"}
                      </TableCell>
                      <TableCell>{getDeadlineDisplay(dsr)}</TableCell>
                      {permissions.canSeeJurisdictionWidget && (
                        <TableCell className="text-sm">{getJurisdictionLabel(dsr.jurisdiction)}</TableCell>
                      )}
                      {permissions.canSeeFlags && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {(dsr.requester?.is_third_party || dsr.requester?.is_agent) && (
                              <div
                                className="h-3 w-3 rounded-full bg-amber-500 flex-shrink-0 border border-amber-600"
                                title="Third-party request"
                              />
                            )}
                            {hasExtension(dsr) && (
                              <div
                                className="h-3 w-3 rounded-full bg-blue-500 flex-shrink-0 border border-blue-600"
                                title="Extension applied"
                              />
                            )}
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-sm">{getStageLabel(dsr)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          {dsr.status && (
                            <Badge
                              className={cn("text-xs", statusStyles[dsr.status]?.bg, statusStyles[dsr.status]?.text)}
                            >
                              {statusLabels[dsr.status] || dsr.status}
                            </Badge>
                          )}
                          {getStalledStatus(dsr) && (
                            <Badge
                              className={cn(
                                "text-xs",
                                getStalledStatus(dsr).isVeryStalled
                                  ? "bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300"
                                  : "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300",
                              )}
                            >
                              {getStalledStatus(dsr).isVeryStalled ? "Stalled" : "No activity"} —{" "}
                              {getStalledStatus(dsr).daysSinceUpdate}d
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {isViewer ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/compliance/dsr/workflow/${dsr.id}`)}
                            className="text-primary hover:text-primary"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openSummary(dsr)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Summary
                              </DropdownMenuItem>
                              {!isReadOnly && permissions.canOpenEdit && (
                                <DropdownMenuItem onClick={() => navigate(`/compliance/dsr/workflow/${dsr.id}`)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Full Request
                                </DropdownMenuItem>
                              )}
                              {permissions.canExport && (
                                <DropdownMenuItem
                                  onClick={(e) => handleExportDocx(dsr.id, e)}
                                  data-testid={`export-dsr-${dsr.id}`}
                                >
                                  <FileDown className="h-4 w-4 mr-2" />
                                  Export DOCX
                                </DropdownMenuItem>
                              )}
                              {!isReadOnly && permissions.canOpenEdit && (
                                <DropdownMenuItem onClick={() => handleDelete(dsr.id)} className="text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Bottom Row - Activity Log & Compliance Reminders (always visible) */}
        {dsrs.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ActivityLogWidget />
            <ComplianceRemindersWidget
              jurisdiction={selectedDsr?.jurisdiction || dsrs[0]?.jurisdiction || "JM"}
              dsr={selectedDsr || dsrs[0]}
            />
          </div>
        )}

        {/* Summary Dialog */}
        <Dialog open={summaryDialogOpen} onOpenChange={setSummaryDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                DSR Summary
              </DialogTitle>
              <DialogDescription>{selectedDsr?.dsr_reference}</DialogDescription>
            </DialogHeader>

            {selectedDsr && (
              <div className="space-y-4">
                {/* Key Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Requester</p>
                    <p className="font-medium">{selectedDsr.requester?.name || "-"}</p>
                    <p className="text-sm text-muted-foreground">{selectedDsr.requester?.email || ""}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Request Type</p>
                    <Badge variant="outline">
                      {requestTypeLabels[selectedDsr.request_type] || selectedDsr.request_type}
                    </Badge>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Date Received</p>
                    <p className="font-medium">
                      {selectedDsr.date_received ? format(new Date(selectedDsr.date_received), "PPP") : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Deadline</p>
                    <div className="font-medium">{getDeadlineDisplay(selectedDsr)}</div>
                  </div>
                </div>

                {/* Summary */}
                {selectedDsr.request_summary && (
                  <div>
                    <p className="text-xs text-muted-foreground">Request Summary</p>
                    <p className="text-sm mt-1">{selectedDsr.request_summary}</p>
                  </div>
                )}

                {/* Status Update */}
                <div className="pt-4 border-t">
                  <p className="text-xs text-muted-foreground mb-2">Update Status</p>
                  <Select value={selectedDsr.status} onValueChange={(val) => handleStatusChange(selectedDsr, val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Identity Verification */}
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm">Identity Verified</span>
                  <Badge variant={selectedDsr.identity_verification?.verified ? "success" : "secondary"}>
                    {selectedDsr.identity_verification?.verified ? "Yes" : "No"}
                  </Badge>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setSummaryDialogOpen(false)}>
                Close
              </Button>
              <Button
                onClick={() => {
                  setSummaryDialogOpen(false);
                  navigate(`/compliance/dsr/workflow/${selectedDsr?.id}`);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Full Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
